import React, { useMemo, useState } from "react";
import { Lock, Check, Clock, X } from "lucide-react";

/**
 * PrivateGalleryCard - Consent-based access request UI
 * 
 * Displays the private gallery status and allows users to request access.
 * No paywall - pure consent-based.
 * 
 * @param {string} ownerDisplayName - Display name of the gallery owner
 * @param {string} ownerUserId - User ID of the gallery owner
 * @param {string} status - Access status: "none" | "pending" | "approved" | "denied"
 * @param {number} privateMediaCount - Number of private media items
 * @param {function} onRequestAccess - Callback: (ownerUserId, message) => Promise
 * @param {string} className - Optional additional CSS classes
 */
export default function PrivateGalleryCard({
  ownerDisplayName = "This user",
  ownerUserId,
  status = "none", // "none" | "pending" | "approved" | "denied"
  privateMediaCount = 0,
  onRequestAccess,
  className = "",
}) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const title = useMemo(() => {
    if (status === "pending") return "Private Gallery — request sent";
    if (status === "denied") return "Private Gallery — not shared";
    if (status === "approved") return "Private Gallery — access granted";
    return "Private Gallery";
  }, [status]);

  const subtitle = useMemo(() => {
    if (privateMediaCount > 0) return `${privateMediaCount} private item${privateMediaCount === 1 ? "" : "s"}`;
    return "Private photos and/or video";
  }, [privateMediaCount]);

  const canRequest = status === "none" || status === "denied";
  const disabled = !canRequest || sending;

  async function handleRequest() {
    if (!onRequestAccess || !ownerUserId) return;
    setError(null);
    try {
      setSending(true);
      await onRequestAccess(ownerUserId, message);
      setSent(true);
    } catch (err) {
      setError(err?.message || "Failed to send request. Please try again.");
    } finally {
      setSending(false);
    }
  }

  // Status badge styles
  const getBadgeStyles = () => {
    switch (status) {
      case "approved":
        return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
      case "pending":
        return "bg-amber-500/10 text-amber-400 border-amber-500/30";
      case "denied":
        return "bg-rose-500/10 text-rose-400 border-rose-500/30";
      default:
        return "bg-slate-800 text-slate-400 border-slate-700";
    }
  };

  const getBadgeIcon = () => {
    switch (status) {
      case "approved":
        return <Check className="w-3 h-3" />;
      case "pending":
        return <Clock className="w-3 h-3" />;
      case "denied":
        return <X className="w-3 h-3" />;
      default:
        return <Lock className="w-3 h-3" />;
    }
  };

  const getBadgeText = () => {
    switch (status) {
      case "approved":
        return "Approved";
      case "pending":
        return "Pending";
      case "denied":
        return "Denied";
      default:
        return "Locked";
    }
  };

  return (
    <div className={`rounded-2xl border border-slate-800 bg-slate-900/60 p-4 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-sm font-semibold text-slate-100">{title}</div>
            <div className="mt-0.5 text-xs text-slate-500">
              {subtitle} • Visible only with the owner&apos;s approval
            </div>
          </div>
        </div>
        <div className={`text-xs rounded-full border px-2.5 py-1 flex items-center gap-1 ${getBadgeStyles()}`}>
          {getBadgeIcon()}
          <span>{getBadgeText()}</span>
        </div>
      </div>

      {status === "approved" ? (
        <div className="mt-3 text-xs text-emerald-400 flex items-center gap-1.5">
          <Check className="w-3.5 h-3.5" />
          Access approved — private media will appear below.
        </div>
      ) : (
        <div className="mt-3">
          <div className="text-xs text-slate-400">
            Ask {ownerDisplayName} to share. They can approve, deny, or revoke later.
          </div>

          {canRequest && !sent && (
            <div className="mt-3 space-y-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                className="w-full rounded-xl border border-slate-700 bg-slate-800/50 p-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-rose-500/40 resize-none"
                placeholder="Optional message (e.g., &quot;Can you share your private photos?&quot;)"
                maxLength={800}
              />
              <button
                type="button"
                onClick={handleRequest}
                disabled={disabled}
                className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                  disabled 
                    ? "bg-slate-800 text-slate-500 cursor-not-allowed" 
                    : "bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-400 hover:to-pink-400 shadow-md"
                }`}
              >
                {sending ? "Sending…" : "Request Access"}
              </button>
              {error && (
                <div className="text-xs text-rose-400 mt-1">{error}</div>
              )}
              <div className="text-[11px] text-slate-600 text-center">
                No paywall. No auto-share. Owner consent only.
              </div>
            </div>
          )}

          {(sent || status === "pending") && (
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-400">
              <Clock className="w-3.5 h-3.5" />
              {sent ? "Request sent! Waiting for approval." : "Waiting for approval."}
            </div>
          )}

          {status === "denied" && !sent && (
            <div className="mt-3 text-xs text-slate-500">
              Your previous request wasn&apos;t approved. You can try again after some time.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
